import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseSwipeToCloseOptions {
  onClose: () => void;
  threshold?: number;
  enabled?: boolean;
  hapticFeedback?: boolean;
  contentRef?: React.RefObject<HTMLElement>;
}

export interface UseSwipeToCloseReturn {
  swipeOffset: number;
  touchHandlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
  };
  isDragging: boolean;
}

/**
 * Hook for handling swipe-down-to-close gesture on bottom sheets
 * Provides visual feedback during swipe and triggers close when threshold is exceeded
 */
export function useSwipeToClose({
  onClose,
  threshold = 100,
  enabled = true,
  hapticFeedback = true,
  contentRef,
}: UseSwipeToCloseOptions): UseSwipeToCloseReturn {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const touchStartY = useRef<number | null>(null);
  const currentOffset = useRef<number>(0);
  const isSwipingVertically = useRef<boolean | null>(null);

  // Check if we're at the top of scrollable content
  const isAtTopOfContent = useCallback(() => {
    if (!contentRef?.current) return true;
    const element = contentRef.current;
    // Check if the element or its scrollable parents are at the top
    return element.scrollTop === 0;
  }, [contentRef]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!enabled) return;

    touchStartY.current = e.touches[0].clientY;
    currentOffset.current = 0;
    isSwipingVertically.current = null;
    setSwipeOffset(0);
    setIsDragging(false);
  }, [enabled]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!enabled || touchStartY.current === null) return;

    const touchCurrentY = e.touches[0].clientY;
    const deltaY = touchCurrentY - touchStartY.current;

    // Determine swipe direction on first significant movement
    if (isSwipingVertically.current === null) {
      if (Math.abs(deltaY) > 10) {
        // Only allow downward swipes (positive deltaY) for closing
        isSwipingVertically.current = deltaY > 0;
      }
    }

    // Only handle downward vertical swipes
    if (isSwipingVertically.current && deltaY > 0) {
      // Check if we're at the top of content or if user is pulling from drag handle area
      const target = e.target as HTMLElement;
      const isDragHandle = target.closest('[data-slot="sheet-drag-handle"]') !== null;
      const atTop = isAtTopOfContent();

      if (atTop || isDragHandle) {
        e.preventDefault();
        setIsDragging(true);

        // Use requestAnimationFrame for smooth visual updates
        requestAnimationFrame(() => {
          setSwipeOffset(deltaY);
          currentOffset.current = deltaY;
        });
      }
    }
  }, [enabled, isAtTopOfContent]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!enabled || touchStartY.current === null) return;

    // Check if threshold exceeded
    if (currentOffset.current > threshold) {
      // Trigger haptic feedback
      if (hapticFeedback && navigator.vibrate) {
        navigator.vibrate(15);
      }

      // Close the sheet
      onClose();
    }

    // Reset all tracking values
    touchStartY.current = null;
    currentOffset.current = 0;
    isSwipingVertically.current = null;
    setSwipeOffset(0);
    setIsDragging(false);
  }, [enabled, threshold, hapticFeedback, onClose]);

  // Stable refs for latest callback values so native listeners don't go stale
  const onCloseRef = useRef(onClose);
  const thresholdRef = useRef(threshold);
  const hapticRef = useRef(hapticFeedback);
  onCloseRef.current = onClose;
  thresholdRef.current = threshold;
  hapticRef.current = hapticFeedback;

  // Track the actual DOM element in state so the listener effect re-runs
  // when the portal mounts a new element (e.g. sheet reopen).
  const [element, setElement] = useState<HTMLElement | null>(null);

  // Poll for the ref's DOM element - needed because the portal may mount
  // after the initial render, and ref.current changes don't trigger re-renders.
  useEffect(() => {
    if (!enabled || !contentRef) return;

    // If already available, set immediately
    if (contentRef.current) {
      setElement(contentRef.current);
    }

    // Poll at ~60fps until the element appears, then watch for removal/replacement
    let rafId: number;
    const poll = () => {
      const el = contentRef.current;
      setElement((prev) => (prev !== el ? el : prev));
      rafId = requestAnimationFrame(poll);
    };
    rafId = requestAnimationFrame(poll);

    return () => cancelAnimationFrame(rafId);
  }, [enabled, contentRef]);

  // Attach native touch listeners to the resolved DOM element.
  useEffect(() => {
    if (!enabled || !element) return;

    // Reset gesture state when element changes (sheet reopened)
    touchStartY.current = null;
    currentOffset.current = 0;
    isSwipingVertically.current = null;

    const handleTouchStartNative = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
      currentOffset.current = 0;
      isSwipingVertically.current = null;
      setSwipeOffset(0);
      setIsDragging(false);
    };

    const handleTouchMoveNative = (e: TouchEvent) => {
      if (touchStartY.current === null) return;

      const touchCurrentY = e.touches[0].clientY;
      const deltaY = touchCurrentY - touchStartY.current;

      if (isSwipingVertically.current === null) {
        if (Math.abs(deltaY) > 10) {
          isSwipingVertically.current = deltaY > 0;
        }
      }

      if (isSwipingVertically.current && deltaY > 0) {
        const target = e.target as HTMLElement;
        const isDragHandle = target.closest('[data-slot="sheet-drag-handle"]') !== null;
        const atTop = element.scrollTop === 0;

        if (atTop || isDragHandle) {
          e.preventDefault();
          setIsDragging(true);

          requestAnimationFrame(() => {
            setSwipeOffset(deltaY);
            currentOffset.current = deltaY;
          });
        }
      }
    };

    const handleTouchEndNative = () => {
      if (touchStartY.current === null) return;

      if (currentOffset.current > thresholdRef.current) {
        if (hapticRef.current && navigator.vibrate) {
          navigator.vibrate(15);
        }
        onCloseRef.current();
      }

      touchStartY.current = null;
      currentOffset.current = 0;
      isSwipingVertically.current = null;
      setSwipeOffset(0);
      setIsDragging(false);
    };

    element.addEventListener('touchstart', handleTouchStartNative, { passive: true });
    element.addEventListener('touchmove', handleTouchMoveNative, { passive: false });
    element.addEventListener('touchend', handleTouchEndNative, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStartNative);
      element.removeEventListener('touchmove', handleTouchMoveNative);
      element.removeEventListener('touchend', handleTouchEndNative);
    };
  }, [enabled, element]);

  return {
    swipeOffset,
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    isDragging,
  };
}
